// src/pages/Dungeon.tsx
import React, { useState, useEffect } from 'react'
import api from '../api/axios'
import { useNavigate } from 'react-router-dom'
import './Dungeon.css'
import enterBtnImg   from '../assets/dungeon/button.png'
import attackImg     from '../assets/dungeon/attack.png'
import defenseImg    from '../assets/dungeon/defense.png'
import counterImg    from '../assets/dungeon/counter.png'

type DungeonStatus = {
  completed1: boolean
  completed2: boolean
  cooldownRemaining: number
}

type EnterResp = {
  dungeonLevel: 1 | 2
  playerHp: number
  monsterHp: number
  floorText: string
  cooldownRemaining: number
}

type ActionResp = {
  monsterHp: number
  playerHp: number
  outcome: string
  finished?: boolean
  victory?: boolean
}

export default function DungeonPage() {
  const navigate = useNavigate()
  const [status, setStatus]   = useState<DungeonStatus | null>(null)
  const [session, setSession] = useState<EnterResp | null>(null)
  const [log, setLog]         = useState<ActionResp[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get<DungeonStatus>('/dungeon')
      .then(r => setStatus(r.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const enter = (level: 1|2) => {
    api.post<EnterResp>('/dungeon/enter',{ level })
      .then(r => { setSession(r.data); setLog([]) })
      .catch(err => alert(err.response?.data?.error||err.message))
  }

  const act = (action: 'attack'|'dodge'|'counter') => {
    api.post<ActionResp>('/dungeon/action',{ action })
      .then(r => {
        setLog(old => [...old, r.data])
        setSession(prev =>
          prev
            ? { ...prev, playerHp: r.data.playerHp, monsterHp: r.data.monsterHp }
            : null
        )
        if (r.data.finished) {
          api.get<DungeonStatus>('/dungeon').then(r2 => setStatus(r2.data))
        }
      })
      .catch(err => alert(err.response?.data?.error||err.message))
  }

  return (
    <div className="dungeon-page">
      <div className="dungeon-container">

        {loading && (
          <p className="d-loading">Loading dungeon status…</p>
        )}

        {!loading && !session && status && (
          <>
            <h2>Dungeon</h2>

            <section className="d-section">
              <h3>Level 1</h3>
              {status.completed1 ? (
                <div className="d-cleared">
                  You’ve already cleared Level 1.<br/>
                  To run again, get a <strong>Dungeon Reset</strong> in the{' '}
                  <button
                    className="d-btn enter"
                    style={{ backgroundImage:`url(${enterBtnImg})` }}
                    onClick={()=>navigate('/shop')}
                  >
                    Shop
                  </button>
                </div>
              ) : (
                <button
                  className="d-btn enter"
                  style={{ backgroundImage:`url(${enterBtnImg})` }}
                  onClick={()=>enter(1)}
                  disabled={status.cooldownRemaining > 0}
                >
                  {status.cooldownRemaining > 0
                    ? `Cooldown: ${status.cooldownRemaining}s`
                    : 'Enter Level 1'}
                </button>
              )}
            </section>

            <section className="d-section">
              <h3>Level 2</h3>
              {status.completed2 ? (
                <div className="d-cleared">
                  You’ve already cleared Level 2.<br/>
                  To run again, get a <strong>Dungeon Reset</strong> in the{' '}
                  <button
                    className="d-btn enter"
                    style={{ backgroundImage:`url(${enterBtnImg})` }}
                    onClick={()=>navigate('/shop')}
                  >
                    Shop
                  </button>
                </div>
              ) : (
                <button
                  className="d-btn enter"
                  style={{ backgroundImage:`url(${enterBtnImg})` }}
                  onClick={()=>enter(2)}
                  disabled={!status.completed1 || status.cooldownRemaining > 0}
                >
                  {!status.completed1
                    ? 'Unlock after Level 1'
                    : status.cooldownRemaining > 0
                      ? `Cooldown: ${status.cooldownRemaining}s`
                      : 'Enter Level 2'}
                </button>
              )}
            </section>
          </>
        )}

        {!loading && session && (
          <div className="d-session">
            <p className="d-floor"><em>{session.floorText}</em></p>
            <p className="d-hp">
              You: {session.playerHp} HP | Monster: {session.monsterHp} HP
            </p>

            {!log.some(e=>e.finished) ? (
              <div className="d-actions">
                <button
                  className="d-btn action"
                  style={{ backgroundImage:`url(${attackImg})` }}
                  onClick={()=>act('attack')}
                >
                  Attack
                </button>
                <button
                  className="d-btn action"
                  style={{ backgroundImage:`url(${defenseImg})` }}
                  onClick={()=>act('dodge')}
                >
                  Defend
                </button>
                <button
                  className="d-btn action"
                  style={{ backgroundImage:`url(${counterImg})` }}
                  onClick={()=>act('counter')}
                >
                  Counter
                </button>
              </div>
            ) : (
              <button
                className="d-btn enter"
                style={{ backgroundImage:`url(${enterBtnImg})` }}
                onClick={()=>setSession(null)}
              >
                Back to Menu
              </button>
            )}

            <ul className="d-log">
              {log.map((e,i) => (
                <li key={i}>
                  {e.outcome}
                  {e.finished && e.victory  && <strong> 🎉 You cleared!</strong>}
                  {e.finished && !e.victory && <strong> ❌ Defeated</strong>}
                </li>
              ))}
            </ul>
          </div>
        )}

      </div>
    </div>
  )
}
